// CursoCompletadoSection.js — El bloque de «¡Felicitaciones!» que aparece
// cuando la persona termina el curso de esa página, con la caja que la invita a
// responder la encuesta de satisfacción mientras no la haya respondido.
//
// Las dos cosas son condicionales de verdad: la sección entera no se pinta si
// el curso no está completo, y la caja de la encuesta desaparece en cuanto se
// responde. Los textos, colores y destinos salen de la config de la sección.
//
// Puede vigilar UN curso (`documento_id`, como siempre) o VARIOS (`cursos`: una
// entrada por curso con su propio mensaje). Con varios: quien termina uno ve el
// mensaje de ese curso, y quien los termina todos ve un único mensaje de cierre
// (`texto_todos`) en vez de repetir uno por curso.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BACKEND_BASE, mentor2Service, encuestaService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { buildBg } from '../bgHelpers';
import EncuestaModal from '../../EncuestaModal';

const Wrapper = styled.section`
  width: 100%;
  padding: ${p => p.$padding || '10px 2rem 70px'};
  background: ${p => p.$bg || '#ffffff'};
`;

const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '1120px'};
  margin: 0 auto;
  padding: 0 1.5rem;
  box-sizing: border-box;
`;

/* Caja de la encuesta: recuadro con el aviso y el botón. */
const CajaEncuesta = styled.div`
  border: ${p => p.$borde || '1px solid #e2e8f0'};
  border-radius: ${p => p.$radio || '4px'};
  background: ${p => p.$bg || 'transparent'};
  padding: ${p => p.$padding || '26px 20px 30px'};
  margin: ${p => p.$margen || '0 0 60px'};
  text-align: center;
`;

const AvisoEncuesta = styled.p`
  margin: 18px 0 0;
  color: ${p => p.$color || '#12345c'};
  font-size: ${p => p.$size || '19px'};
  font-weight: ${p => p.$peso || 700};
  line-height: 1.4;
`;

const BotonEncuesta = styled.button`
  margin-top: 16px;
  background: ${p => p.$bg || '#0a7ab5'};
  color: ${p => p.$color || '#ffffff'};
  border: ${p => p.$borde || 'none'};
  border-radius: ${p => p.$radio || '999px'};
  padding: ${p => p.$padding || '11px 26px'};
  font-family: inherit;
  font-size: ${p => p.$size || '15px'};
  font-weight: ${p => p.$peso || 600};
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const Titulo = styled.h2`
  margin: ${p => p.$margen || '0 0 14px'};
  text-align: center;
  color: ${p => p.$color || '#12345c'};
  font-size: ${p => p.$size || '46px'};
  font-weight: ${p => p.$peso || 700};
  font-family: ${p => p.$font || 'inherit'};
  @media (max-width: 600px) { font-size: ${p => p.$sizeMovil || '32px'}; }
`;

const Texto = styled.div`
  margin: 0 auto;
  max-width: ${p => p.$maxWidth || '1000px'};
  text-align: center;
  color: ${p => p.$color || '#1e293b'};
  font-size: ${p => p.$size || '19px'};
  line-height: ${p => p.$lineHeight || '1.5'};
  strong { font-weight: 700; }
`;

const Acciones = styled.div`
  margin-top: ${p => p.$margen || '26px'};
  text-align: center;
`;

const Boton = styled.button`
  background: ${p => p.$bg || '#e01b3c'};
  color: ${p => p.$color || '#ffffff'};
  border: ${p => p.$borde || 'none'};
  border-radius: ${p => p.$radio || '999px'};
  padding: ${p => p.$padding || '14px 44px'};
  font-family: inherit;
  font-size: ${p => p.$size || '20px'};
  font-weight: ${p => p.$peso || 600};
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
`;

const IconoImg = styled.img`
  width: ${p => p.$ancho || '58px'};
  height: auto;
  display: inline-block;
`;

// Icono por defecto de la caja: el aspa dentro del círculo del diseño.
const IconoAspa = ({ color = '#12345c', size = 58 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <circle cx="24" cy="24" r="21" stroke={color} strokeWidth="3.5" />
    <path d="M16 16l16 16M32 16L16 32" stroke={color} strokeWidth="4.5" strokeLinecap="round" />
  </svg>
);

const CursoCompletadoSection = ({ config, proyecto, tl }) => {
  const c = config || {};
  const t = (v, def = '') => (tl ? tl(v, def) : (v || def));
  const navigate = useNavigate();
  const { user } = useAuth();

  const minimo = Number(c.porcentaje_minimo || 100);

  // Cursos vigilados. Con `cursos` son varios, cada uno con su mensaje; sin
  // ella, el `documento_id` de siempre con los textos de la propia sección.
  const cursos = useMemo(() => {
    const lista = Array.isArray(c.cursos) ? c.cursos : [];
    const limpios = lista
      .filter(x => x && x.documento_id)
      .map(x => ({ ...x, documento_id: String(x.documento_id) }));
    if (limpios.length) return limpios;
    return c.documento_id
      ? [{ documento_id: String(c.documento_id), titulo: c.titulo, texto: c.texto,
           url_boton: c.url_boton, texto_boton: c.texto_boton }]
      : [];
  }, [c.cursos, c.documento_id, c.titulo, c.texto, c.url_boton, c.texto_boton]);

  const idsKey = cursos.map(x => x.documento_id).join(',');

  const [progreso, setProgreso] = useState(null);   // {documento_id: %}
  const [encuesta, setEncuesta] = useState(null);   // {requiere, encuesta}
  const [modal, setModal] = useState(false);
  const [enviando, setEnviando] = useState(false);

  // Avance de los cursos que vigila la sección.
  useEffect(() => {
    if (!user || !idsKey) { setProgreso({}); return; }
    let vivo = true;
    mentor2Service.progresoProyecto(idsKey.split(','))
      .then(res => { if (vivo) setProgreso(res.data?.progreso || {}); })
      .catch(() => { if (vivo) setProgreso({}); });
    return () => { vivo = false; };
  }, [user, idsKey]);

  const estaCompleto = (id) => progreso !== null && Number(progreso[id] || 0) >= minimo;
  const completados = cursos.filter(x => estaCompleto(x.documento_id));
  // Con varios cursos terminados todos, se felicita una sola vez.
  const todos = cursos.length > 1 && completados.length === cursos.length;
  const completado = completados.length > 0;
  const claveCert = c.certificado_key || 'default';
  const conEncuesta = c.mostrar_encuesta === true;

  // Estado de la encuesta: solo hace falta preguntarlo si el curso ya está
  // completo y la sección la ofrece.
  const cargarEncuesta = useCallback(() => {
    if (!user || !conEncuesta || !proyecto?.id) return;
    encuestaService.estado(proyecto.id, claveCert)
      .then(res => setEncuesta(res.data || null))
      .catch(() => setEncuesta(null));
  }, [user, conEncuesta, proyecto?.id, claveCert]);

  useEffect(() => { if (completado) cargarEncuesta(); }, [completado, cargarEncuesta]);

  if (!user || !completado) return null;

  const responder = async (respuestas) => {
    setEnviando(true);
    try {
      await encuestaService.responder(proyecto.id, claveCert, respuestas);
    } finally {
      setEnviando(false);
    }
  };

  const irAlDestino = (url) => {
    if (!url) return;
    if (/^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener');
    else navigate(url);
  };

  // Qué se felicita: el cierre común si están todos, si no lo de cada curso
  // terminado (con lo de la sección como respaldo).
  const mensajes = todos
    ? [{
        clave: '__todos__',
        titulo: c.titulo_todos || c.titulo,
        texto: c.texto_todos,
        url_boton: c.url_boton_todos || c.url_boton,
        texto_boton: c.texto_boton_todos || c.texto_boton,
      }]
    : completados.map(x => ({
        clave: x.documento_id,
        titulo: x.titulo || c.titulo,
        texto: x.texto || c.texto,
        url_boton: x.url_boton || c.url_boton,
        texto_boton: x.texto_boton || c.texto_boton,
      }));

  const iconoUrl = c.encuesta_icono
    ? (c.encuesta_icono.startsWith('http') ? c.encuesta_icono : `${BACKEND_BASE}/${c.encuesta_icono.replace(/^\//, '')}`)
    : null;

  return (
    <Wrapper $bg={buildBg(c, '#ffffff')} $padding={c.padding}>
      <Inner $maxWidth={c.ancho_maximo}>
        {conEncuesta && encuesta?.requiere && (
          <CajaEncuesta
            $borde={c.encuesta_borde}
            $radio={c.encuesta_radio}
            $bg={c.encuesta_fondo}
            $padding={c.encuesta_padding}
            $margen={c.encuesta_margen}
          >
            {iconoUrl
              ? <IconoImg src={iconoUrl} alt="" $ancho={c.encuesta_icono_ancho} />
              : <IconoAspa color={c.encuesta_icono_color || c.color_titulo || '#12345c'} size={parseInt(c.encuesta_icono_ancho, 10) || 58} />}
            <AvisoEncuesta
              $color={c.encuesta_color_texto}
              $size={c.encuesta_tamano_texto}
              $peso={c.encuesta_peso_texto}
            >
              {t(c.encuesta_texto, 'Complete la encuesta de satisfacción para acceder a la descarga de su certificado.')}
            </AvisoEncuesta>
            <BotonEncuesta
              type="button"
              /* Con `encuesta_url` la encuesta vive en su propia página; sin
                 ella se abre aquí mismo en una ventana emergente. */
              onClick={() => (c.encuesta_url ? navigate(c.encuesta_url) : setModal(true))}
              $bg={c.encuesta_boton_fondo}
              $color={c.encuesta_boton_color}
              $borde={c.encuesta_boton_borde}
              $radio={c.encuesta_boton_radio}
              $padding={c.encuesta_boton_padding}
              $size={c.encuesta_boton_tamano}
            >
              {t(c.encuesta_boton_texto, 'IR A ENCUESTA')}
            </BotonEncuesta>
          </CajaEncuesta>
        )}

        {mensajes.map(m => (
          <React.Fragment key={m.clave}>
            {t(m.titulo) !== '' && (
              <Titulo
                $color={c.color_titulo}
                $size={c.tamano_titulo}
                $sizeMovil={c.tamano_titulo_movil}
                $peso={c.peso_titulo}
                $font={c.fuente_titulo}
                $margen={c.margen_titulo}
                dangerouslySetInnerHTML={{ __html: t(m.titulo, '¡Felicitaciones!') }}
              />
            )}
            {t(m.texto) !== '' && (
              <Texto
                $color={c.color_texto}
                $size={c.tamano_texto}
                $lineHeight={c.interlineado_texto}
                $maxWidth={c.ancho_texto}
                dangerouslySetInnerHTML={{ __html: t(m.texto) }}
              />
            )}
            {c.mostrar_boton !== false && t(m.texto_boton) !== '' && m.url_boton && (
              <Acciones $margen={c.margen_boton}>
                <Boton
                  type="button"
                  onClick={() => irAlDestino(m.url_boton)}
                  $bg={c.color_boton}
                  $color={c.color_texto_boton}
                  $borde={c.borde_boton}
                  $radio={c.radio_boton}
                  $padding={c.padding_boton}
                  $size={c.tamano_boton}
                  $peso={c.peso_boton}
                >
                  {t(m.texto_boton, 'Ingresar')}
                </Boton>
              </Acciones>
            )}
          </React.Fragment>
        ))}
      </Inner>

      {modal && encuesta?.encuesta && (
        <EncuestaModal
          encuesta={encuesta.encuesta}
          color={c.encuesta_boton_fondo || proyecto?.color_primario || '#0f355b'}
          submitting={enviando}
          onSubmit={responder}
          onFinalizar={() => { setModal(false); cargarEncuesta(); }}
          onClose={() => { setModal(false); cargarEncuesta(); }}
        />
      )}
    </Wrapper>
  );
};

export default CursoCompletadoSection;
